import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'idealSleep', 'idealSleepDisplay', 'dayInput',
    'sleepDebt', 'statusContainer', 'statusDot', 'statusText', 'statusDescription',
    'weeklyAverage', 'recoveryTime', 'recommendations'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const idealSleep = parseFloat(this.idealSleepTarget.value)
    this.idealSleepDisplayTarget.textContent = `${idealSleep.toFixed(1)}h`

    const sleepHours = this.dayInputTargets.map((input) => parseFloat(input.value) || 0)
    const totalSleep = sleepHours.reduce((a, b) => a + b, 0)
    const avgSleep = totalSleep / 7
    const idealTotal = idealSleep * 7
    const debt = Math.max(0, idealTotal - totalSleep)

    this.sleepDebtTarget.textContent = debt.toFixed(1)
    this.weeklyAverageTarget.textContent = avgSleep.toFixed(1)

    // Recovery time (adding 1-2 hours per night)
    const recoveryDays = Math.ceil(debt / 1.5)
    this.recoveryTimeTarget.textContent = recoveryDays

    // Status
    this.updateStatus(debt, avgSleep, idealSleep)
    this.updateRecommendations(debt, avgSleep, idealSleep)
  }

  updateStatus(debt, _avgSleep, _idealSleep) {
    let dotColor; let statusText; let
      description

    if (debt <= 0) {
      dotColor = 'bg-green-500'
      statusText = 'OPTIMAL SLEEP'
      description = "You're getting enough sleep. Keep up the good work!"
    } else if (debt <= 5) {
      dotColor = 'bg-yellow-500'
      statusText = 'MILD SLEEP DEBT'
      description = 'Minor sleep deficit. A couple of good nights will help you recover.'
    } else if (debt <= 10) {
      dotColor = 'bg-orange-500'
      statusText = 'MODERATE SLEEP DEBT'
      description = 'Noticeable deficit. You may experience reduced focus and increased fatigue.'
    } else {
      dotColor = 'bg-red-500'
      statusText = 'SIGNIFICANT SLEEP DEBT'
      description = 'Substantial deficit. Prioritize sleep recovery to restore cognitive function.'
    }

    this.statusDotTarget.className = `w-3 h-3 ${dotColor}`
    this.statusTextTarget.textContent = statusText
    this.statusDescriptionTarget.textContent = description
  }

  updateRecommendations(debt, _avgSleep, _idealSleep) {
    let recommendations = []

    if (debt <= 0) {
      recommendations = [
        'Maintain your current sleep schedule',
        'Continue prioritizing consistent bedtimes',
        'Keep your sleep environment optimized'
      ]
    } else if (debt <= 5) {
      recommendations = [
        `Add 30-60 minutes to your sleep for the next ${Math.ceil(debt)} nights`,
        'Consider a 20-minute power nap if needed',
        'Avoid caffeine after 2pm to improve sleep quality'
      ]
    } else if (debt <= 10) {
      recommendations = [
        `Extend sleep by 1-2 hours nightly for the next ${Math.ceil(debt / 1.5)} days`,
        'Go to bed 30 minutes earlier each night',
        'Limit screen time 1 hour before bed',
        'Consider a sleep-promoting evening routine'
      ]
    } else {
      recommendations = [
        'Make sleep your top priority for the next 1-2 weeks',
        'Aim for 9-10 hours per night until recovered',
        'Avoid scheduling demanding tasks during peak fatigue',
        'Consider speaking with a healthcare provider if chronic'
      ]
    }

    this.recommendationsTarget.innerHTML = recommendations
      .map((rec) => `<li class="flex items-start gap-2"><span class="text-gray-400">></span><span>${rec}</span></li>`)
      .join('')
  }
}
